import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Editor } from '@tinymce/tinymce-react';
import debounce from 'debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import type { BlogPost } from '../../../../@types/BlogPost';
import { BlogPostStatusEnum } from '../../../../@types/BlogPost';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup/Popup.module';
import BlogPostSidebar from './components/BlogPostSidebar';

export default function BlogPostEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(true);
  const [newPostTitle, setNewPostTitle] = useState('');

  const saveBlogPost = useCallback(async(updatedData: Partial<BlogPost> | FormData) => {
    if (!jwt) {return;}

    setIsSaving(true);
    try {
      const isFormData = updatedData instanceof FormData;
      const method = id ? 'PUT' : 'POST';
      const url = id ? `/api/blog-posts/${id}` : '/api/blog-posts';

      let jsonData: Partial<BlogPost>;
      let imageFile: File | null = null;
      
      if (isFormData) {
        const formData = updatedData as FormData;
        jsonData = {
          title: formData.get('title') as string,
          content: formData.get('content') as string,
          status: formData.get('status') as BlogPostStatusEnum,
        };
        imageFile = formData.get('mainImageFile') as File;
      } else {
        // Only include required fields when sending JSON
        jsonData = {
          title: updatedData.title || blogPost?.title || '',
          content: updatedData.content || blogPost?.content || '',
          status: updatedData.status || blogPost?.status || BlogPostStatusEnum.DRAFT,
        };
      }

      // First save the blog post data
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error((errorData && errorData.errors && errorData.errors.title) || 'Une erreur est survenue lors de la sauvegarde de l\'article');
      }
      
      let data = await response.json();

      // If there's an image file, upload it in a separate request
      if (imageFile && data.id) {
        const imageFormData = new FormData();
        imageFormData.append('mainImageFile', imageFile);

        const imageResponse = await fetch(`/api/blog-posts/${data.id}/image`, {
          method: 'POST',
          headers: {
            'Authorization': JWTAuthorization(jwt),
          },
          body: imageFormData,
        });

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json();
          throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Une erreur est survenue lors du téléchargement de l\'image');
        }

        data = await imageResponse.json();
      }

      setBlogPost(data);
      setLastSaved(new Date());
      
      if (!id) {
        navigate(ROUTES.ADMIN_BLOG_POST_EDIT.replace(':id', data.id.toString()));
        showToast('success', 'Article créé.');
      }
    } catch (error) {
      console.error('Failed to save blog post :', error);
      showToast('error', error.message || 'Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  }, [blogPost, id, jwt, navigate]);

  const debouncedSave = useMemo(
    () =>
      debounce((contentDebounced: string, saveFunction: (data: Partial<BlogPost>) => void) => {
        saveFunction({ content: contentDebounced });
      }, 5000),
    [],
  );

  const fetchBlogPost = async() => {
    if (!id || !jwt) {return;}
    try {
      const response = await fetch(`/api/blog-posts/${id}`, JWTHeader(jwt));
      if (!response.ok) {throw new Error('Failed to fetch blog post');}
      const data = await response.json();
      setBlogPost(data);
      setContent(data.content);
    } catch (error) {
      console.error('Failed to fetch blog post :', error);
      showToast('error', 'Une erreur est survenue lors du chargement de l\'article.');
    }
  };

  useEffect(() => {
    if (!jwt || isInitialized) {return;}

    if (id) {
      fetchBlogPost();
      setIsInitialized(true);
    } else {
      setIsCreateModalOpen(true);
    }
  }, [id, jwt, isInitialized]);

  const handleEditorChange = (newContent: string) => {
    setContent(newContent);
    setBlogPost(prev => prev ? { ...prev, content: newContent } : null);
    
    if (id && newContent !== blogPost?.content) {
      debouncedSave(newContent, saveBlogPost);
    }
  };

  const handleAIContentUpdate = (newContent: string) => {
    setContent(newContent);
    setBlogPost(prev => prev ? { ...prev, content: newContent } : null);
    if (id) {
      saveBlogPost({ content: newContent });
    }
  };

  useEffect(() => {
    return () => {
      debouncedSave.clear();
    };
  }, [debouncedSave]);

  const handleTitleSubmit = async() => {
    if (!newPostTitle.trim() || !jwt) {return;}

    const formData = new FormData();
    // Only send required fields
    formData.append('title', newPostTitle);
    formData.append('content', '.');
    formData.append('status', BlogPostStatusEnum.DRAFT);
    
    await saveBlogPost(formData);
    setIsCreateModalOpen(false);
    setIsInitialized(true);
  };

  const handleAIContentGenerated = (AIcontent: { title: string; content: string }) => {
    setContent(AIcontent.content);
    setBlogPost(prev => prev ? {
      ...prev,
      title: AIcontent.title,
      content: AIcontent.content,
    } : null);
  };

  const handleFilePicker = (callback: (url: string, meta?: { alt?: string; title?: string }) => void, value: string, meta: { filetype?: string }) => {
    if (meta.filetype === 'image') {
      const searchImages = async(query: string) => {
        try {
          const response = await fetch(
            `https://pixabay.com/api/?key=${process.env.REACT_APP_PIXABAY_API_KEY}&q=${encodeURIComponent(query)}&image_type=photo&per_page=30`,
          );
          const data = await response.json();
          
          const container = document.querySelector('.image-picker-container');
          if (!container) {return;}
          container.innerHTML = '';

          if (data.hits) {
            (data.hits as { previewURL: string; largeImageURL: string; tags: string }[]).forEach((image) => {
              const img = document.createElement('img');
              img.src = image.previewURL;
              img.style.cssText = 'width: 100%; cursor: pointer; border-radius: 4px; margin-bottom: 8px;';
              
              img.onclick = () => {
                callback(image.largeImageURL, { 
                  alt: image.tags,
                  title: image.tags,
                });
                window.tinymce?.activeEditor?.windowManager.close();
              };

              container.appendChild(img);
            });
          }
        } catch (error) {
          console.error('Error fetching images :', error);
        }
      };

      window.tinymce?.activeEditor?.windowManager.open({
        title: 'Select Image',
        size: 'large',
        body: {
          type: 'panel',
          items: [
            {
              type: 'input',
              name: 'search',
              label: 'Rechercher',
              placeholder: 'Taper pour rechercher des images...',
            },
            {
              type: 'htmlpanel',
              html: '<div class="image-picker-container" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; max-height: 400px; overflow-y: auto; padding: 10px;"></div>',
            },
          ],
        },
        buttons: [
          {
            type: 'cancel',
            text: 'Fermer',
          },
        ],
        onAction: (api: { getData: () => { search?: string } }) => {
          const data = api.getData();
          searchImages(data.search ?? '');
        },
        onChange: (api: { getData: () => { search?: string } }, details: { name?: string }) => {
          if (details.name === 'search') {
            const data = api.getData();
            searchImages(data.search ?? '');
          }
        },
        onSubmit: (api: { close: () => void }) => {
          api.close();
        },
      });

      // Initial search
      searchImages('blog');
    }
  };

  if (!id && isCreateModalOpen) {
    return (
      <Popup
        isOpen={true}
        title="Créer un nouvel article de blog"
        width="450px"
        disableCloseButton
      >
        <div className="flex flex-col gap-4 px-4">
          <h3 className="mt-8 text-lg font-semibold">Titre du nouvel article :</h3>
          <input
            type="text"
            value={newPostTitle}
            onChange={(e) => setNewPostTitle(e.target.value)}
            autoFocus
            className="mt-2 mb-4 w-full rounded-xl border p-2"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={handleTitleSubmit}
              disabled={!newPostTitle.trim()}
              className={`
                rounded-lg px-4 py-2 text-white
                ${newPostTitle.trim() 
        ? 'bg-rgpd-blue-dark hover:bg-rgpd-blue-darker' 
        : 'cursor-not-allowed bg-gray-300'}
              `}
            >
              Créer
            </button>
          </div>
        </div>
      </Popup>
    );
  }

  if (!blogPost) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="flex-1 overflow-auto">
        <div className="mb-4 flex flex-col items-start justify-between lg:flex-row lg:items-center">
          <button
            onClick={() => navigate(ROUTES.ADMIN_BLOG_POSTS)}
            className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="mr-2 h-5 w-5" />
            Retour
          </button>
          <h1 className="text-2xl font-bold text-white">
            {id ? 'Modification de l\'article' : 'Création d\'un article'}
          </h1>
        </div>

        <div className="mb-4 lg:hidden">
          <BlogPostSidebar
            blogPost={blogPost}
            onUpdate={saveBlogPost}
            isSaving={isSaving}
            lastSaved={lastSaved}
            onAIContentGenerated={handleAIContentGenerated}
            onAIContentAdjusted={handleAIContentUpdate}
          />
        </div>
        
        <Editor
          apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
          value={content}
          onEditorChange={handleEditorChange}
          init={{
            relative_urls: false,
            remove_script_host: false,
            convert_urls: false,
            document_base_url: window.location.origin,
            language: 'fr_FR',
            height: 'calc(90dvh - 100px)',
            resize: false,
            menubar: true,
            content_css: '/build/tinymce-content.css',
            formats: {
              h1: { block: 'h1', classes: 'text-4xl font-bold mb-4' },
              h2: { block: 'h2', classes: 'text-3xl font-bold mb-3' },
              h3: { block: 'h3', classes: 'text-2xl font-bold mb-3' },
              h4: { block: 'h4', classes: 'text-xl font-bold mb-2' },
            },
            style_formats: [
              { title: 'Paragraph', format: 'p' },
              { title: 'Heading 1', format: 'h1' },
              { title: 'Heading 2', format: 'h2' },
              { title: 'Heading 3', format: 'h3' },
              { title: 'Heading 4', format: 'h4' },
            ],
            content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
            plugins: [
              'accordion',
              'anchor',
              'autolink',
              'autosave',
              'charmap',
              'code',
              'codesample',
              'directionality',
              'emoticons',
              'fullscreen',
              'help',
              'image',
              'importcss',
              'insertdatetime',
              'link',
              'lists',
              'advlist',
              'media',
              'nonbreaking',
              'pagebreak',
              'preview',
              'quickbars',
              'save',
              'searchreplace',
              'table',
              'visualblocks',
              'visualchars',
              'wordcount',
            ],
            toolbar: [
              'undo redo | fontfamily | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify',
              'bullist numlist outdent indent | forecolor backcolor | link image media | emoticons charmap',
              'fullscreen preview save | searchreplace | table | code codesample | pagebreak nonbreaking',
              'visualblocks visualchars | help',
            ].join(' | '),
            autosave_ask_before_unload: true,
            autosave_interval: '30s',
            autosave_prefix: 'tinymce-autosave-{path}{query}-{id}-',
            image_advtab: true,
            link_context_toolbar: true,
            lists_indent_on_tab: true,
            quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
            quickbars_insert_toolbar: 'quickimage quicktable',
            contextmenu: 'link image table',
            codesample_languages: [
              { text: 'HTML/XML', value: 'markup' },
              { text: 'JavaScript', value: 'javascript' },
              { text: 'CSS', value: 'css' },
              { text: 'PHP', value: 'php' },
              { text: 'Python', value: 'python' },
              { text: 'Java', value: 'java' },
              { text: 'C', value: 'c' },
              { text: 'C++', value: 'cpp' },
            ],
            font_family_formats: 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; -apple-system=-apple-system; BlinkMacSystemFont=BlinkMacSystemFont; Segoe UI=segoe ui; Roboto=roboto; Helvetica Neue=helvetica neue; Arial=arial,sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
            file_picker_callback: handleFilePicker,
          }}
        />
      </div>

      <div className={'mt-[58px] hidden lg:block'}>
        <BlogPostSidebar
          blogPost={blogPost}
          onUpdate={saveBlogPost}
          isSaving={isSaving}
          lastSaved={lastSaved}
          onAIContentGenerated={handleAIContentGenerated}
          onAIContentAdjusted={handleAIContentUpdate}
        />
      </div>
    </div>
  );
}